/* ==============================================================
   RAKE — animated gradient (WebGL2)
   A full-screen fragment shader: value noise, warped by a swirl
   loop, quantised into a soft check pattern, then mapped across
   three brand colours. Two instances run it — one behind the
   intro's dispersing words, one behind the hero — from a single
   parameter set, so they read as the same light.

   Everything here is progressive: no WebGL2, a shader that won't
   compile, or a lost context all leave the flat ink background
   the page already had. Reduced motion gets one still frame.
   ============================================================== */
(() => {
  'use strict';

  // Bichrome, like the rest of the site. Ink ground, a lifted grey
  // where the light lands, one thin bone streak for the thread.
  const RAKE = {
    color1: '#0c0c10',
    color2: '#3c3c46',
    color3: '#f4f3f0',
    rotation: -50,
    proportion: 26,
    scale: 0.42,
    distortion: 6,
    swirl: 62,
    swirlIterations: 10,
    softness: 100,
    offset: -299,
    shapeSize: 34
  };

  const VERT = `#version 300 es
in vec4 a_position;
void main() { gl_Position = a_position; }`;

  const FRAG = `#version 300 es
precision highp float;

uniform float u_time;
uniform float u_pixelRatio;
uniform vec2 u_resolution;

uniform float u_scale;
uniform float u_rotation;
uniform vec4 u_color1;
uniform vec4 u_color2;
uniform vec4 u_color3;
uniform float u_proportion;
uniform float u_softness;
uniform float u_shapeScale;
uniform float u_distortion;
uniform float u_swirl;
uniform float u_swirlIterations;

out vec4 fragColor;

#define TWO_PI 6.28318530718
#define PI 3.14159265358979323846

vec2 rotate(vec2 uv, float th) {
  return mat2(cos(th), sin(th), -sin(th), cos(th)) * uv;
}

float random(vec2 st) {
  return fract(sin(dot(st.xy, vec2(12.9898, 78.233))) * 43758.5453123);
}

float noise(vec2 st) {
  vec2 i = floor(st);
  vec2 f = fract(st);
  float a = random(i);
  float b = random(i + vec2(1.0, 0.0));
  float c = random(i + vec2(0.0, 1.0));
  float d = random(i + vec2(1.0, 1.0));

  vec2 u = f * f * (3.0 - 2.0 * f);

  float x1 = mix(a, b, u.x);
  float x2 = mix(c, d, u.x);
  return mix(x1, x2, u.y);
}

vec4 blend_colors(vec4 c1, vec4 c2, vec4 c3, float mixer, float edgesWidth, float edge_blur) {
  vec3 color1 = c1.rgb * c1.a;
  vec3 color2 = c2.rgb * c2.a;
  vec3 color3 = c3.rgb * c3.a;

  float r1 = smoothstep(.0 + .35 * edgesWidth, .7 - .35 * edgesWidth + .5 * edge_blur, mixer);
  float r2 = smoothstep(.3 + .35 * edgesWidth, 1. - .35 * edgesWidth + edge_blur, mixer);

  vec3 blended_color_2 = mix(color1, color2, r1);
  float blended_opacity_2 = mix(c1.a, c2.a, r1);

  vec3 c = mix(blended_color_2, color3, r2);
  float o = mix(blended_opacity_2, c3.a, r2);
  return vec4(c, o);
}

void main() {
  vec2 uv = gl_FragCoord.xy / u_resolution.xy;

  float t = .5 * u_time;
  float noise_scale = .0005 + .006 * u_scale;

  uv -= .5;
  uv *= (noise_scale * u_resolution);
  uv = rotate(uv, u_rotation * .5 * PI);
  uv /= u_pixelRatio;
  uv += .5;

  float n1 = noise(uv * 1. + t);
  float n2 = noise(uv * 2. - t);
  float angle = n1 * TWO_PI;
  uv.x += 4. * u_distortion * n2 * cos(angle);
  uv.y += 4. * u_distortion * n2 * sin(angle);

  float iterations_number = ceil(clamp(u_swirlIterations, 1., 30.));
  for (float i = 1.; i <= iterations_number; i++) {
    uv.x += clamp(u_swirl, 0., 2.) / i * cos(t + i * 1.5 * uv.y);
    uv.y += clamp(u_swirl, 0., 2.) / i * cos(t + i * 1. * uv.x);
  }

  float proportion = clamp(u_proportion, 0., 1.);

  vec2 checks_uv = uv * (.5 + 3.5 * u_shapeScale);
  float shape = .5 + .5 * sin(checks_uv.x) * cos(checks_uv.y);
  float mixer = shape + .48 * sign(proportion - .5) * pow(abs(proportion - .5), .5);

  vec4 color_mix = blend_colors(u_color1, u_color2, u_color3, mixer,
                                1. - clamp(u_softness, 0., 1.), .01 + .01 * u_scale);

  fragColor = vec4(color_mix.rgb, color_mix.a);
}`;

  const hex = (h) => [
    parseInt(h.slice(1, 3), 16) / 255,
    parseInt(h.slice(3, 5), 16) / 255,
    parseInt(h.slice(5, 7), 16) / 255,
    1
  ];

  const calm = matchMedia('(prefers-reduced-motion: reduce)');

  function mount(host, speed) {
    if (!host) return null;
    const canvas = host.querySelector('canvas');
    if (!canvas) return null;

    let gl;
    try {
      gl = canvas.getContext('webgl2', { alpha: true, premultipliedAlpha: true, antialias: true });
    } catch (e) { gl = null; }
    if (!gl) return null;

    const compile = (type, src) => {
      const sh = gl.createShader(type);
      gl.shaderSource(sh, src);
      gl.compileShader(sh);
      if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) { gl.deleteShader(sh); return null; }
      return sh;
    };

    const vs = compile(gl.VERTEX_SHADER, VERT);
    const fs = vs && compile(gl.FRAGMENT_SHADER, FRAG);
    if (!vs || !fs) return null;

    const program = gl.createProgram();
    gl.attachShader(program, vs);
    gl.attachShader(program, fs);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) return null;
    gl.useProgram(program);

    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(program, 'a_position');
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);

    const u = (n) => gl.getUniformLocation(program, n);
    const U = {
      time: u('u_time'), resolution: u('u_resolution'), pixelRatio: u('u_pixelRatio'),
      scale: u('u_scale'), rotation: u('u_rotation'),
      color1: u('u_color1'), color2: u('u_color2'), color3: u('u_color3'),
      proportion: u('u_proportion'), softness: u('u_softness'),
      shapeScale: u('u_shapeScale'), distortion: u('u_distortion'),
      swirl: u('u_swirl'), swirlIterations: u('u_swirlIterations')
    };

    // Constants: uploaded once rather than every frame.
    const c1 = hex(RAKE.color1), c2 = hex(RAKE.color2), c3 = hex(RAKE.color3);
    gl.uniform4f(U.color1, c1[0], c1[1], c1[2], c1[3]);
    gl.uniform4f(U.color2, c2[0], c2[1], c2[2], c2[3]);
    gl.uniform4f(U.color3, c3[0], c3[1], c3[2], c3[3]);
    gl.uniform1f(U.scale, RAKE.scale);
    gl.uniform1f(U.rotation, RAKE.rotation * Math.PI / 180);
    gl.uniform1f(U.proportion, RAKE.proportion / 100);
    gl.uniform1f(U.softness, RAKE.softness / 100);
    gl.uniform1f(U.shapeScale, RAKE.shapeSize / 100);
    gl.uniform1f(U.distortion, RAKE.distortion / 50);
    gl.uniform1f(U.swirl, RAKE.swirl / 100);
    gl.uniform1f(U.swirlIterations, RAKE.swirl === 0 ? 0 : RAKE.swirlIterations);

    // A background has no business rendering at 3x on a phone.
    const ratio = () => Math.min(window.devicePixelRatio || 1, 2);

    let w = 0, h = 0;
    const size = () => {
      const r = ratio();
      const cw = Math.max(1, Math.round(canvas.clientWidth * r));
      const ch = Math.max(1, Math.round(canvas.clientHeight * r));
      if (cw === w && ch === h) return;
      w = canvas.width = cw;
      h = canvas.height = ch;
      gl.viewport(0, 0, w, h);
      gl.uniform2f(U.resolution, w, h);
      gl.uniform1f(U.pixelRatio, r);
    };

    const draw = (seconds) => {
      size();
      gl.uniform1f(U.time, seconds * (speed / 100) * 5 + RAKE.offset * 0.01);
      gl.drawArrays(gl.TRIANGLES, 0, 6);
    };

    let raf = 0, running = false, dead = false;
    let clock = 0, last = 0;
    // Two independent gates: on screen, and the page in front. The loop runs
    // only when both agree, so returning to a tab scrolled past the hero
    // doesn't quietly restart it.
    let onScreen = true, awake = !document.hidden;

    const ro = new ResizeObserver(() => { if (!running) draw(clock); });
    ro.observe(canvas);

    const frame = (now) => {
      if (dead) return;
      // Elapsed time is accumulated rather than read from the clock, so a
      // pause (hidden tab, scrolled away) resumes instead of jumping.
      clock += Math.min((now - last) / 1000, 0.05);
      last = now;
      draw(clock);
      raf = requestAnimationFrame(frame);
    };

    const start = () => {
      if (running || dead || calm.matches || !onScreen || !awake) return;
      running = true;
      last = performance.now();
      raf = requestAnimationFrame(frame);
    };
    const stop = () => {
      running = false;
      cancelAnimationFrame(raf);
    };
    const sync = () => (onScreen && awake ? start() : stop());
    const destroy = () => {
      dead = true;
      stop();
      ro.disconnect();
      gl.deleteProgram(program);
      gl.deleteShader(vs);
      gl.deleteShader(fs);
      gl.deleteBuffer(buffer);
      gl.getExtension('WEBGL_lose_context')?.loseContext();
    };

    canvas.addEventListener('webglcontextlost', (e) => { e.preventDefault(); destroy(); });

    draw(clock);
    host.setAttribute('data-lit', '');

    return {
      start, stop, destroy,
      seen: (yes) => { onScreen = yes; sync(); },
      woke: (yes) => { awake = yes; sync(); }
    };
  }

  const intro = mount(document.getElementById('introField'), 11);
  const hero = mount(document.getElementById('heroField'), 9);

  // The intro's field is only ever seen for a few seconds; hand the GPU
  // back the moment the words are done rather than leaving it spinning.
  if (intro) {
    intro.start();
    addEventListener('bsw:intro-done', () => intro.destroy(), { once: true });
  }

  if (hero) {
    // Only run while the hero is actually on screen.
    const io = new IntersectionObserver(
      (entries) => entries.forEach((en) => hero.seen(en.isIntersecting)),
      { rootMargin: '10% 0px' }
    );
    io.observe(document.getElementById('heroField'));
  }

  // Nothing animates behind a tab nobody is looking at.
  document.addEventListener('visibilitychange', () => {
    const on = !document.hidden;
    if (intro) intro.woke(on);
    if (hero) hero.woke(on);
  });

  calm.addEventListener?.('change', () => {
    if (!calm.matches) return;
    if (intro) intro.stop();
    if (hero) hero.stop();
  });
})();
